/**
 * A route test's Creative step (user, 19/10/2026: "the route test could use
 * the text ranking — the best texts for the test drops"). A route test
 * compares gateways, so every test drop sends the SAME texts: the best N of
 * the text ranking (the master's formula and rules, the same campaign) or N
 * fresh AI texts, written once. The choice is the route test's own (saved
 * with its route test settings); the live list shows today's actual texts.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { FlaskConical } from 'lucide-react';
import { type DropGenTextMix, DROP_GEN_TEXTS_PER_DROP_MAX, lockRouteTestRanking, routeTestTexts, routeTestTextsWords } from '@trafficpulse/shared';

import { HintDot, NumberField, StepCard } from '@/components/ui';
import { cn } from '@/lib/utils';

import { useTextRanking } from '../../../../hooks/useTextRanking';
import { ChoiceChips } from '../../general/controls/ChoiceChips';
import { MINIMAL_FIELD } from '../../general/controls/field-style';
import type { ContainerEditor } from '../../state/useContainerEditor';
import { poolOf, type PoolText, todayTextsLine } from './today-texts';

type Choice = 'best' | 'ai';
const OPTIONS = [
  { value: 'best', label: 'Best ranked texts' },
  { value: 'ai', label: 'Fresh AI texts' },
] as const;
const HINT =
  'A route test compares gateways, so its test drops all send the same texts: only the gateway changes. Best ranked texts come from the text ranking (the master’s formula and rules, on the same campaign); when it has fewer, fresh AI texts fill in. Fresh AI texts are written once for the day.';

export const RouteTestTextsStep: FC<{ index: number; editor: ContainerEditor; countryKey: string }> = ({ index, editor, countryKey }) => {
  const settings = editor.draft.gateway.ranking;
  const mix = routeTestTexts(settings.routeTestTexts);
  const ranking = useTextRanking({ countryKey, campaignWiserId: editor.draft.campaignWiserId, ranking: editor.draft.creative.ranking });
  const pool = ranking.data ? poolOf(ranking.data) : null;
  const save = (next: DropGenTextMix) => editor.gatewayOp({ kind: 'ranking', ranking: lockRouteTestRanking({ ...settings, routeTestTexts: next }) });
  return (
    <StepCard index={index} icon={FlaskConical} title="Texts on test drops" summary={`${routeTestTextsWords(mix)} · the same on every test drop`}>
      <div className="flex flex-col gap-2" data-testid="route-test-texts">
        <TextChoiceRow mix={mix} onChange={save} />
        <p key={`${mix.proven}-${mix.ai}`} className="text-[11px] leading-snug text-muted-foreground duration-300 animate-in fade-in-0 motion-reduce:animate-none">
          {todayTextsLine(mix, pool, ranking.isError)}
        </p>
        {mix.proven > 0 && pool && pool.length > 0 ? <TodayTexts texts={pool.slice(0, mix.proven)} /> : null}
      </div>
    </StepCard>
  );
};

const TextChoiceRow: FC<{ mix: DropGenTextMix; onChange: (mix: DropGenTextMix) => void }> = ({ mix, onChange }) => {
  const choice: Choice = mix.proven > 0 ? 'best' : 'ai';
  const count = mix.proven > 0 ? mix.proven : mix.ai;
  const set = (c: Choice, n: number) => onChange(c === 'best' ? { proven: n, ai: 0 } : { proven: 0, ai: n });
  const clamp = (v: number) => Math.max(1, Math.min(DROP_GEN_TEXTS_PER_DROP_MAX, Math.floor(v) || 1));
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
        Every test drop sends <HintDot label={HINT} />
      </span>
      <ChoiceChips<Choice> options={OPTIONS} value={choice} onChange={(c) => set(c, count)} ariaLabel="The texts of the test drops" />
      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <NumberField
          value={count}
          onChange={(v) => set(choice, clamp(v))}
          min={1}
          max={DROP_GEN_TEXTS_PER_DROP_MAX}
          aria-label="Texts on each test drop"
          className={cn(MINIMAL_FIELD, 'h-8 w-14 text-center text-sm')}
        />
        texts
      </span>
    </div>
  );
};

const TodayTexts: FC<{ texts: PoolText[] }> = ({ texts }) => (
  <ol className="flex flex-col gap-1 rounded-md bg-muted/40 px-2.5 py-2 duration-300 animate-in fade-in-0 motion-reduce:animate-none">
    {texts.map((t, i) => (
      <li key={`${i}-${t.text}`} className="flex min-w-0 items-baseline gap-2 text-xs" title={t.text}>
        <span className="w-4 shrink-0 text-right font-mono text-[10px] text-muted-foreground">{i + 1}</span>
        <span className="min-w-0 flex-1 truncate">{t.text}</span>
        {t.score !== null ? <span className="shrink-0 font-mono text-[10px] tabular-nums text-muted-foreground">{t.score.toFixed(1)}</span> : null}
      </li>
    ))}
  </ol>
);
